import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { acquisitionApi } from "../../api/acquisition";
import {
  acquisitionSessionId,
  acquisitionVisitorId,
} from "../../utils/acquisitionFunnel";
import { publicRegistrationPath } from "../../utils/publicRegistration";
import PublicAuthLayout from "../../components/home/PublicAuthLayout";
import { EntryIcon } from "../../components/home/PublicEntryUI";

function loginFieldErrors({ email, password }) {
  const errors = {};
  // Existing accounts may use legacy/internal domains. Keep the original
  // login check rather than imposing the stricter new-registration rules.
  if (
    !String(email || "")
      .trim()
      .includes("@")
  ) {
    errors.email = "Ingresá el email de tu cuenta.";
  }
  // # LOGIN: authenticate existing credentials. New-password length rules
  // belong to registration/recovery and must not block older passwords here.
  if (!password) errors.password = "Ingresá tu contraseña.";
  return errors;
}

export default function LoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = React.useState({ email: "", password: "" });
  const [errors, setErrors] = React.useState({});
  const [serverError, setServerError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);
  const submittingRef = React.useRef(false);
  const submittedRef = React.useRef(false);
  const formRef = React.useRef(null);
  const errorRef = React.useRef(null);
  const registerHref = publicRegistrationPath(
    location.search,
    "login-register",
  );
  const homeHref = publicRegistrationPath(location.search, "", "/");
  const message = location.state?.message || "";
  const requestedSeverity = location.state?.messageSeverity || "success";
  const messageSeverity = ["success", "warning", "error", "info"].includes(
    requestedSeverity,
  )
    ? requestedSeverity
    : "info";
  const fromLocation = location.state?.from;
  const from =
    fromLocation?.pathname?.startsWith("/") &&
    !fromLocation.pathname.startsWith("//")
      ? `${fromLocation.pathname}${fromLocation.search || ""}${fromLocation.hash || ""}`
      : "/dashboard";

  React.useEffect(() => {
    if (!authLoading && user && !submittedRef.current)
      navigate(from, { replace: true });
  }, [authLoading, from, navigate, user]);
  React.useEffect(() => {
    if (serverError) errorRef.current?.focus();
  }, [serverError]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setServerError("");
  };
  const handleBlur = (event) => {
    const key = event.target.name;
    setErrors((previous) => ({
      ...previous,
      [key]: loginFieldErrors(form)[key] || "",
    }));
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    // # PUBLIC AUTH: keep the form visible while /auth/me resolves, and avoid
    // a late initial session response overwriting a just-authenticated user.
    if (submittingRef.current || authLoading) return;
    if (user) {
      navigate(from, { replace: true });
      return;
    }
    const nextErrors = loginFieldErrors(form);
    setErrors(nextErrors);
    const firstInvalid = ["email", "password"].find((key) => nextErrors[key]);
    if (firstInvalid) {
      formRef.current?.elements.namedItem(firstInvalid)?.focus();
      return;
    }
    submittingRef.current = true;
    submittedRef.current = true;
    setSubmitting(true);
    setServerError("");
    try {
      await login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      // Preserve returning-client attribution without waiting for telemetry.
      acquisitionApi
        .updateSession(acquisitionSessionId(), {
          visitorId: acquisitionVisitorId(),
          audience: "authenticated",
        })
        .catch(() => null);
      navigate(from, { replace: true });
    } catch (error) {
      setServerError(
        error.message || "No pudimos iniciar sesión. Intentá de nuevo.",
      );
      submittedRef.current = false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <PublicAuthLayout
      homeHref={homeHref}
      accountHref={registerHref}
      accountPrompt="¿Todavía no tenés cuenta?"
      accountLabel="Crear cuenta"
      eyebrow="TU ESPACIO DE TRABAJO"
      title="Ingresar"
      titleId="wp-login-title"
      subtitle="Volvé a tu panel y seguí donde lo dejaste."
      storyEyebrow="WEB, VENTAS Y GESTIÓN"
      storyTitle={
        <>
          Todo conectado.
          <br />
          <span>Seguí desde acá.</span>
        </>
      }
      storyDescription="Tu página web, tus clientes y la gestión diaria, en el mismo lugar."
      benefits={[
        "Volvé a tus proyectos.",
        "Revisá tu actividad.",
        "Continuá con tu negocio.",
      ]}
      storyNote="Entrá con el email y la contraseña de tu cuenta de WebProfit."
    >
      {message && (
        <div
          className={`wp-auth-message wp-auth-message-${messageSeverity}`}
          role={messageSeverity === "error" ? "alert" : "status"}
        >
          {message}
        </div>
      )}
      {serverError && (
        <div
          className="wp-signup-alert"
          role="alert"
          tabIndex={-1}
          ref={errorRef}
        >
          {serverError}
        </div>
      )}
      <form
        className="wp-signup-form"
        onSubmit={handleSubmit}
        noValidate
        ref={formRef}
        aria-busy={submitting || authLoading}
      >
        <div className="wp-signup-field">
          <label htmlFor="wp-login-email">Email</label>
          <input
            id="wp-login-email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="nombre@correo.com"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={submitting}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "wp-login-email-error" : undefined}
          />
          {errors.email && (
            <p className="wp-signup-field-error" id="wp-login-email-error">
              {errors.email}
            </p>
          )}
        </div>
        <div className="wp-signup-field">
          <label htmlFor="wp-login-password">Contraseña</label>
          <div className="wp-signup-password-wrap">
            <input
              id="wp-login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoCapitalize="none"
              spellCheck={false}
              value={form.password}
              onChange={handleChange}
              onBlur={(event) => {
                handleBlur(event);
                setCapsLock(false);
              }}
              onKeyUp={(event) =>
                setCapsLock(Boolean(event.getModifierState?.("CapsLock")))
              }
              required
              disabled={submitting}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={
                [
                  errors.password && "wp-login-password-error",
                  capsLock && "wp-login-caps",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
            />
            <button
              type="button"
              aria-label={
                showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
              }
              aria-pressed={showPassword}
              onClick={() => setShowPassword((value) => !value)}
              disabled={submitting}
            >
              <EntryIcon name={showPassword ? "eyeOff" : "eye"} size={20} />
            </button>
          </div>
          {errors.password && (
            <p className="wp-signup-field-error" id="wp-login-password-error">
              {errors.password}
            </p>
          )}
          {capsLock && (
            <p
              className="wp-signup-field-note"
              id="wp-login-caps"
              role="status"
            >
              Tenés las mayúsculas activadas.
            </p>
          )}
        </div>
        <div className="wp-auth-recovery">
          <Link to="/olvide-contrasena">¿Olvidaste tu contraseña?</Link>
        </div>
        <button
          type="submit"
          className="wp-entry-primary wp-signup-submit"
          disabled={submitting || authLoading}
        >
          {submitting ? (
            <>
              <span className="wp-entry-spinner" aria-hidden="true" />
              Ingresando…
            </>
          ) : authLoading ? (
            "Comprobando sesión…"
          ) : (
            <>
              Ingresar
              <EntryIcon size={19} />
            </>
          )}
        </button>
        <p className="wp-signup-reassurance">
          <EntryIcon name="lock" size={14} />
          Accedés con tu cuenta de WebProfit.
        </p>
      </form>
      <p className="wp-signup-mobile-login">
        ¿No tenés cuenta? <Link to={registerHref}>Crear cuenta</Link>
      </p>
    </PublicAuthLayout>
  );
}
